import { types, flow, getParent, getRoot, getSnapshot } from "mobx-state-tree"
import { reaction } from "mobx"
import { trackGoogleConversion } from '~/services/tracking/googleAdsConversion'
import Router from "next/router"
import {
  PARKING_FLOW_STEPS,
  getNextStep,
  getPreviousStep,
  getStep
} from "~/components/parking/config/tools"
import { PARKING_FLOW_TYPE, PAYMENT_PROVIDERS } from "~/utilities/constants"
import {
  chargeParkingTickets,
  getParkingGuiltyChargeAmount,
  getParkingFees,
  savePersistedApplicationState,
  getSessionStatusFromCKO,
  finalize3DSChallengeStripe,
} from "~/services/payments"
import { errorToShow } from "~/utilities/error"
import UserPlate from '~/stores/models/Plate'
import TRACKING from '~/services/tracking'
import { getMessageId } from '~/services/tracking/posthog'
import { getUserSessionToken } from "./parkingConfig"
import TicketResult from "~/stores/parkingTicket/flow/TicketResult"
import * as ParkingTicketService from "~/services/parking/ticket"
import { loadFlowState, saveFlowState, clearFlowState } from "~/utilities/flowPersistence"

export const FlowStore = types
  .model("FlowStore", {
    entryType: types.optional(
      types.enumeration(Object.keys(PARKING_FLOW_TYPE)),
      PARKING_FLOW_TYPE.both
    ),
    type: types.maybeNull(
      // do not allow both type here. user must have one
      types.enumeration(
        Object.keys(PARKING_FLOW_TYPE).filter(
          type => type !== PARKING_FLOW_TYPE.both
        )
      )
    ),
    isUserPreferredFightOrPay: types.optional(types.boolean, false),
    isPayParkingFromMobile: types.optional(types.boolean, false),
    currentStepSlug: types.maybeNull(types.string),
    plate: types.maybeNull(types.reference(UserPlate))
  })
    .actions(self => ({
      afterCreate() {
        // Persist the flow slice to sessionStorage whenever relevant state
        // changes (debounced 500ms). Set up once on the client; mirrors the
        // trafficLookupForm autosave. Lives in afterCreate (not setType) so it
        // also covers the Results page, which never enters a flow.
        if (process.browser) {
          reaction(
            () => JSON.stringify(self.persistableSlice),
            () => saveFlowState(self.persistableSlice),
            { delay: 500 }
          )
        }
      },
      initialize: flow(function* initialize() {
        const root = getRoot(self)
        const { isPayNYCFlow } = root.ui
        const source = isPayNYCFlow ? 'payNYC': 'webapp'
        const {user} = root.auth
        const resultsStore = getParent(self).results
        // totalSelectedPayableAmount is a Number and totalSelectedFightableAmount is a String
        if (self.isPayParkingFromMobile) {
          // Parse the ticketObjectIds query parameter into an array
          let { target: ticketObjectIds } = Router.query
          try {
            ticketObjectIds = JSON.parse(ticketObjectIds)
          } catch (error) {
            console.error('Error parsing ticketObjectIds:', error);
            ticketObjectIds = []; // Default to an empty array if parsing fails
          }
          self.setType(PARKING_FLOW_TYPE.pay)
          const selectedTickets = []
          yield Promise.all(
              ticketObjectIds.map((objectId) =>
                  ParkingTicketService.get(objectId, user.sessionToken)
                      .then(ticket => {
                        // A missing/deleted ticket resolves to null — skip it
                        // instead of crashing on ticket.plateNumber below.
                        if (!ticket) return
                        selectedTickets.push(TicketResult.create({
                          objectId: ticket.objectId,
                          ticketID: ticket.ticketID,
                          plateNumber: ticket.plateNumber,
                          state: ticket.state,
                          amountDue: ticket.amountDue,
                        }))
                      })
              )
          )
          resultsStore.setResults(selectedTickets)
          resultsStore.setPayableSelected(selectedTickets)

          const totalAmountDue = resultsStore.totalSelectedPayableAmount
          const shouldUserBeEvervaulted = yield root.payment.shouldUserBeEvervaulted()
          const pricing = yield getParkingFees(null, source, totalAmountDue, user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault || shouldUserBeEvervaulted)
          resultsStore.setServiceFee(pricing.serviceFee)
          resultsStore.setAdditionalFeePerTicket(pricing.additionalFeePerTicket)
          resultsStore.setCreditCardFee(pricing.creditCardFee)
          const plateSource = selectedTickets.find((t) => t && t.plateNumber && t.state)
          if (plateSource) {
            yield self.setPlateIfExistent(plateSource.plateNumber, plateSource.state)
          }
          Router.push('/parking/flow')
        }
        else {
          const totalAmountDue = resultsStore.totalSelectedPayableAmount
          const shouldUserBeEvervaulted = yield root.payment.shouldUserBeEvervaulted()
          const pricing = yield getParkingFees(null, source, totalAmountDue, self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault || shouldUserBeEvervaulted)
          resultsStore.setServiceFee(pricing.serviceFee)
          resultsStore.setAdditionalFeePerTicket(pricing.additionalFeePerTicket)
          resultsStore.setCreditCardFee(pricing.creditCardFee)
        }
      })
    }))
  .views(self => ({
    get landingPage() {
      switch (self.entryType) {
        default:
        case PARKING_FLOW_TYPE.both:
          return "/parking/submit"
        case PARKING_FLOW_TYPE.fight:
          return "/parking/fight"
        case PARKING_FLOW_TYPE.pay:
          return "/parking/pay"
      }
    },
    get isFightFlow() {
      return self.type === PARKING_FLOW_TYPE.fight
    },
    get isPayFlow() {
      return self.type === PARKING_FLOW_TYPE.pay
    },
    get isRobotPayFlow() {
      return self.type === PARKING_FLOW_TYPE.robotpay
    },
    get ticketState() {
      if (self.plate && self.plate.state) return self.plate.state
      const hasFlowType =
        self.isPayFlow || self.isFightFlow || self.isRobotPayFlow
      if (!hasFlowType) return null
      const firstTicket = self.ticketsForFlow[0]
      return (firstTicket && firstTicket.state) || null
    },
    get currentStep() {
      return getStep(self.currentStepSlug, self.stack)
    },
    get user() {
      const root = getRoot(self)
      return root.auth.user
    },
    get ticketsForFlow() {
      const parent = getParent(self)
      if (self.isPayFlow || self.isUserPreferredFightOrPay || self.isRobotPayFlow) {
        return parent.results.paySelected
      }
      if (self.isFightFlow) {
        return parent.results.fightSelected
      }
      throw new Error("No flow type")
    },
    get objectIdsForFlow() {
      return self.ticketsForFlow.map((ticket) => ticket.objectId)
    },
    get ticketIdsForFlow() {
      return self.ticketsForFlow.map((ticket) => ticket.ticketID)
    },
    // Minimal, serializable slice needed to rebuild the flow after a refresh.
    // View (not action) so the autosave reaction tracks its dependencies.
    get persistableSlice() {
      // Full snapshot per ticket — payability/fightability (status, fightable,
      // blocked, isPaid, processPayment) and display fields (violationName,
      // fineAmount, ...) are views over model state, so a hand-picked subset
      // would leave restored tickets unpayable/unfightable and mis-rendered.
      const ticketSnapshot = (ticket) => getSnapshot(ticket)
      // ticketsForFlow throws for type 'both' / null — guard with the flow-type
      // views so the autosave reaction (set up in afterCreate, when type is
      // still 'both') does not throw.
      const hasFlowType =
        self.isPayFlow || self.isFightFlow || self.isRobotPayFlow
      const { found } = getParent(self).results
      return {
        type: self.type,
        entryType: self.entryType,
        step: self.currentStepSlug,
        tickets: hasFlowType ? self.ticketsForFlow.map(ticketSnapshot) : [],
        // Full search-results list so the Results page can rebuild on refresh.
        results: found.map(ticketSnapshot),
        plateNumber: (self.plate && self.plate.plateNumber) || null,
        plateState: (self.plate && self.plate.state) || null,
        // Fees are set in flow.initialize (Results click), which does not re-run
        // on refresh — persist them so the checkout total is correct.
        serviceFee: getParent(self).results.serviceFee,
        creditCardFee: getParent(self).results.creditCardFee,
        additionalFeePerTicket: getParent(self).results.additionalFeePerTicket,
      }
    },
  }))
  .volatile(() => ({
    stack: [],
  }))
  .actions((self) => ({
    unSetPlate() {
      self.plate = null
    },
    setPlateIfExistent: flow(function* setPlateIfExistent(plateNumber, state) {
      let selectedOrAddedPlate
      try {
        const plateStore = getRoot(self).plates
        yield plateStore.fetchAll()  // Ensure plates are fetched first
        const existingPlate = plateStore.findPlate(plateNumber, state)
        if (existingPlate) {
          selectedOrAddedPlate = existingPlate
        } else {
          // If plate is not found, add it
          plateStore.addPlate({
            plateNumber,
            state
          })
          selectedOrAddedPlate = plateStore.findPlate(plateNumber, state)
        }
        if (selectedOrAddedPlate) {
          self.unSetPlate()
          self.plate = selectedOrAddedPlate
        } else {
          console.error('Plate was not added or found in the store.')
        }
      } catch (error) {
        console.error('Error getting initial plate:', error)
      }
      return selectedOrAddedPlate
    }),
    canProgress: flow(function* canProgressGen() {
      if (!self.currentStep) return true
      const isValid = yield self.currentStep.validate(self)
      return isValid
    }),
    get3DSVerifyAmount: flow(function* remote3DSInitializeGen() {
      // we need to call save here to create the tickets to get charge amount if the tickets do not exist on the user yet.
      yield self.saveTickets()
      // gets the actual charge amount
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      const source = isPayNYCFlow ? 'payNYC': 'webapp'
      const total = yield getParkingGuiltyChargeAmount(self.objectIdsForFlow, null, source, self.user.email)
      return total
    }),
    // Nonce is passed in from payment store for 3DS. only matters for pay flow.
    handleCheckout: flow(function* handleCheckout(nonce) {
      try {
        let success = false
        yield self.saveTickets()
        const { isPaymentMethodJustAdded } = getRoot(self).payment
        const { auth } = getRoot(self)
        if (!auth.isLoggedIn) {
          // Plate is saved for plate-scoped account features (notifications,
          // Ticket Guardian). It is NOT required to charge — the charge is
          // keyed by ticket objectIds — so a missing plate is best-effort.
          const plate = yield self.savePlate()
          if (!plate) {
            console.warn("Checkout proceeding without a saved plate (none could be derived from the selected tickets).")
          }
        }

        if (self.isFightFlow) {
          // If we made it here for fight flow this is success
          success = true
        }
        // if it is pay flow lets charge for the tickets
        if (self.isPayFlow || self.isRobotPayFlow) {
          if (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault)
            success = true
          else {
            // charge the users tickets
            success = yield self.chargeTicket(self.objectIdsForFlow, nonce, isPaymentMethodJustAdded);
            // refetch the current user to update credit amount on user.
            yield self.user.fetchCurrentUserData()
          }
        }
        // all done
        if (success) {
          self.trackTickets()
        }
        if (success !== "REDIRECTED" && (self.user.paymentProcessor.type !== PAYMENT_PROVIDERS.evervault || self.isFightFlow)) {
          self.nextStep()
        }
      } catch (error) {
        let errorMessage = ""
        if (typeof message === "string") {
          errorMessage = error 
        } else {
          errorMessage = error.toString()
        }
        const { ui } = getRoot(self)
        if (self.isPayFlow && ui.isPayNYCFlow) {
          const resultsStore = getParent(self).results
          TRACKING.paynycParking.payFailure(
            ui.isPayNYCFlow,
            self.user.paymentProcessor.type,
            resultsStore.paySubtotal,
            errorMessage
          )
        }
        self.showErrorToast(errorMessage)
      }
    }),
    trackTickets() {
      const ticketIds = self.ticketsForFlow.map(ticket => ticket.objectId)
      const transactionIdentifier = ticketIds.join(' - ')
      const { ui, auth } = getRoot(self)
      const ga4State =
        (self.plate && self.plate.state) ||
        (self.ticketsForFlow[0] && self.ticketsForFlow[0].state) ||
        undefined
      const resultsStore = getParent(self).results
      const isPayFlowType = self.type === PARKING_FLOW_TYPE.pay
      // Evervault pays through the CityPay bot, which confirms ~60s later over a
      // socket. The GA4 `purchase` used to be deferred to that citypay_paid
      // callback, but the callback only lands for ~60% of checkouts (tab closed,
      // socket timeout, flow reset), so GA4 reported ~40% fewer parking sales
      // than the database. `purchase` now fires here, at checkout success, for
      // every pay flow; the deferred call sites in paymentStore still run and
      // are deduplicated by transaction_id inside googleAnalytics.purchase.
      //
      // Trade-off: this reports the checkout rather than the confirmed city
      // payment, so a charge that later fails still counts (measured ~7% over
      // the confirmed total, against the ~40% that was being lost).
      const isEvervaultPay =
        isPayFlowType && self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault
      const ga4Items = TRACKING.paynycParking.parkingItems(self.ticketsForFlow, self.type)
      TRACKING.parking.submissionConversion(
        transactionIdentifier,
        self.type,
        ui.currentHost,
        ga4State,
        {
          // Real USD charged: paySubtotal already includes the city fine,
          // service fee and card fee, minus credits. Fight flow has no charge.
          value: isPayFlowType ? resultsStore.paySubtotal : undefined,
          items: ga4Items
        }
      )

      if (isPayFlowType && ui.currentHost.includes('paynyctickets.com')) {
        trackGoogleConversion(ticketIds[0], 'parking_nyc_pay', auth.user.sessionToken, resultsStore.paySubtotal)

        if (!isEvervaultPay) {
          TRACKING.paynycParking.paySuccess(
            ui.isPayNYCFlow,
            self.user.paymentProcessor.type,
            resultsStore.paySubtotal,
            resultsStore.totalServiceFee,
            { transactionId: transactionIdentifier, items: ga4Items, state: ga4State }
          )
        }
      }

      if (["communicationsPayableEmail", "communicationsFightableEmail"].includes(auth.user.origin)) {
        TRACKING.parking.paidFromNycEmail(self.entryType, auth.user.origin)
      }
    },
    savePlate: flow(function* savePlate() {
      let createdOrUpdatedPlate = null
      try {
        if (!self.plate) {
          console.warn("Cannot save plate: plate reference is null")
          return null
        }
        if (self.plate.objectId) {
          createdOrUpdatedPlate = yield self.plate.update()
        } else {
          createdOrUpdatedPlate = yield self.plate.save()
        }
      } catch (error) {
        console.error("Error creating plate:", error)
      }
      return createdOrUpdatedPlate
    }),
    saveTickets: flow(function* saveTickets() {
      try {
        const ticketsToSave = self.ticketsForFlow
        for (let i = 0; i < ticketsToSave.length; i += 1) {
          const ticketResult = ticketsToSave[i]
          yield ticketResult.saveTicket(self.type, self.user.sessionToken, self.user.email)
        }
        return true
      } catch (error) {
        const displayError = errorToShow(error, "error saving tickets")
        console.error("error saving tickets: ", error)
        if (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.cko) {
          getRoot(self).payment.remountFlow()
        }
        throw displayError
      }
    }),
    chargeTicket: flow(function* handleChargeTicket(objectIds, nonce, isPaymentMethodJustAdded) {
      const sessionToken = getUserSessionToken(self)
      const root = getRoot(self)
      const { isPayNYCFlow } = root.ui
      const source = isPayNYCFlow ? 'payNYC': 'webapp'
      const { user } = root.auth
      const { cardScheme } = root.payment.cko

      try {
        const paymentResult = yield chargeParkingTickets("PARKING_PAY", objectIds, nonce, sessionToken, source, isPaymentMethodJustAdded, user.email, cardScheme, getMessageId())
        const result = paymentResult.res
        const { credits } = paymentResult
        if (result.client_secret) {
          const stripe = window.Stripe(process.env.STRIPE_PUBLIC_KEY)
          const { error, paymentIntent } = yield stripe.confirmCardPayment(
            result.client_secret
          )
          const status = paymentIntent?.status || "failed"
          if (paymentIntent?.id) {
            yield finalize3DSChallengeStripe(
              objectIds,
              user.email, // For returning users to help identify them in the backend
              paymentIntent.id,
              status,
              paymentIntent,
              credits
            )
          }
          if (error) {
            yield finalize3DSChallengeStripe(
              objectIds,
              user.email, // For returning users to help identify them in the backend
              error?.payment_intent?.id,
              status,
              error,
              credits
            )
            const stripeErrorMessage = `Card authentication error: ${error.message}`
            throw stripeErrorMessage
          }
          if (status !== "succeeded") {
            throw new Error("Payment not completed successfully after 3DS authentication.")
          }
        }
        const tickets = self.ticketsForFlow
        for (let i = 0; i < tickets.length; i += 1) {
          yield tickets[i].fetchTicket(sessionToken)
        }
        if (user.paymentProcessor.type === PAYMENT_PROVIDERS.cko) {
          yield getSessionStatusFromCKO()
          const redirectLink = result?.redirectLink
          if (redirectLink) {
            const ckoSessionId = redirectLink.substring(redirectLink.lastIndexOf("sid_"))
            const applicationState = getSnapshot(getRoot(self))
            yield savePersistedApplicationState(applicationState, ckoSessionId)
            window.location.replace(redirectLink)
            return "REDIRECTED"
          }
        }
      } catch (error) {
        if (user.paymentProcessor.type === PAYMENT_PROVIDERS.cko) {
          getRoot(self).payment.remountFlow()
        }
        console.error("Parking Flow - Error charging tickets: ", error)
        throw error
      }
      return true
    }),
    setEntryType(type) {
      self.entryType = type
      self.stack = []
      self.currentStepSlug = null
      // Entering a landing page = fresh start; drop any saved flow slice.
      self.clearFlowSession()
    },
    nextStep: flow(function* nextStepGen() {
      const canProgress = yield self.canProgress()
      if (!canProgress) return
      const nextStep = getNextStep(self.currentStepSlug, self.stack)
      // getNextStep returns null when there is no following step (e.g. on the
      // terminal step, or after refresh rehydration leaves currentStepSlug/stack
      // inconsistent). Bail instead of dereferencing null — matches previousStep.
      if (!nextStep) return
      self.setStep(nextStep.slug)
    }),
    previousStep() {
      const previousStep = getPreviousStep(self.currentStepSlug, self.stack)
      self.setStep(previousStep ? previousStep.slug : null)
    },
    setRobotPayResults(robotResults, serviceFee, creditCardFee, additionalFee) {
      const resultsStore = getParent(self).results
      resultsStore.setResults(robotResults)
      resultsStore.setPayableSelected(robotResults)
      resultsStore.setServiceFee(serviceFee)
      resultsStore.setCreditCardFee(creditCardFee)
      resultsStore.setAdditionalFeePerTicket(additionalFee)
    },

    setType(type, stepSlug) {
      self.type = type
      if (type) {
        self.stack = PARKING_FLOW_STEPS[type]
        if (stepSlug) {
          self.setStep(stepSlug)
        } else {
          self.setStep(self.stack[0].slug)
        }
      } else {
        // setType(null) is "back to results" — do NOT clear the saved slice:
        // the results list is still relevant for a results-page refresh.
        self.stack = []
        self.currentStepSlug = null
      }
    },
    setIsPayParkingFromMobile(value) {
        self.isPayParkingFromMobile = value
    },
    setStep(slug) {
      self.currentStepSlug = slug
      // Success is terminal — drop the saved slice so a refresh on the success
      // page does not rehydrate a completed checkout.
      if (slug && slug.toLowerCase() === "success") {
        self.clearFlowSession()
      }
    },
    // --- Refresh persistence (sessionStorage, tab-scoped) --------------------
    // The store is rebuilt empty on refresh; afterCreate autosaves the slice
    // (above). Restore actions rebuild from it. No backend, no orphans — dies
    // with the tab (abandon) and is cleared on success / fresh start.
    // Rebuild the flow from a saved slice. Synchronous (tickets are restored
    // from stored snapshots, no network); plate resolution is async and
    // best-effort. Returns false if there is nothing to restore so the caller
    // (flow.jsx guard) can bounce to the landing page.
    restoreFlowState() {
      if (!process.browser) return false
      const slice = loadFlowState()
      if (!slice || !slice.type || !slice.step) return false
      const resultsStore = getParent(self).results
      if (Array.isArray(slice.tickets) && slice.tickets.length) {
        const tickets = slice.tickets
          .filter((t) => t && t.ticketID)
          .map((t) => TicketResult.create(t))
        resultsStore.setResults(tickets)
        resultsStore.setPayableSelected(tickets)
        resultsStore.setFightableSelected(tickets)
      }
      if (slice.serviceFee != null) resultsStore.setServiceFee(slice.serviceFee)
      if (slice.creditCardFee != null) resultsStore.setCreditCardFee(slice.creditCardFee)
      if (slice.additionalFeePerTicket != null) {
        resultsStore.setAdditionalFeePerTicket(slice.additionalFeePerTicket)
      }
      if (slice.entryType) self.entryType = slice.entryType
      // setType rebuilds the volatile stack + step (autosave runs from afterCreate).
      self.setType(slice.type, slice.step)
      if (slice.plateNumber && slice.plateState) {
        // Fire-and-forget; plate populates async (matches mobile flow).
        self.setPlateIfExistent(slice.plateNumber, slice.plateState)
      }
      return true
    },
    clearFlowSession() {
      if (!process.browser) return
      // Clear the saved slice only — keep the autosave reaction alive so the
      // next change (e.g. a new search from the landing page) re-saves.
      clearFlowState()
    },
    // Rebuild the Results page (search results) from a saved slice. Used by the
    // Results component on refresh — `found` is rebuilt from stored snapshots
    // (no network). Returns false if there is nothing to restore.
    restoreResultsState() {
      if (!process.browser) return false
      const slice = loadFlowState()
      if (!slice || !Array.isArray(slice.results) || !slice.results.length) {
        return false
      }
      const resultsStore = getParent(self).results
      const tickets = slice.results
        .filter((t) => t && t.ticketID)
        .map((t) => TicketResult.create(t))
      resultsStore.setResults(tickets)
      if (slice.entryType) self.entryType = slice.entryType
      return true
    },
    setUserPreferredFightOrPay(value) {
      self.isUserPreferredFightOrPay = value
    },
    showErrorToast(message) {
      const rootStore = getRoot(self)
      rootStore.ui.toasts.addToast({
        message,
        type: "ERROR"
      })
    },
  }))
